/**
 * 通用图片URL处理工具
 * 如果传入数组就返回数组，如果传入字符串就返回字符串
 */

// 支持的图片数据类型
type ImageInput = string | { url?: string; image?: string; path?: string } | null | undefined

const DEFAULT_PRODUCTION_FILE_BASE = 'https://game-rental-1366715589.cos.ap-guangzhou.myqcloud.com'

/**
 * 构建完整的图片URL
 * @param input 图片路径（字符串、对象或数组）
 * @returns 完整URL（保持输入类型）
 */
export function buildImageUrl(input: string): string
export function buildImageUrl(input: ImageInput): string
export function buildImageUrl(input: ImageInput[]): string[]
export function buildImageUrl(input: ImageInput | ImageInput[]): string | string[] {
    const configuredPublicBase = (import.meta.env.VITE_PUBLIC_FILE_BASE as string | undefined)?.trim()
    const publicBase = configuredPublicBase || (import.meta.env.PROD ? DEFAULT_PRODUCTION_FILE_BASE : '')

    // 从各种格式中提取路径字符串
    const extractPath = (item: ImageInput): string => {
        if (!item) return ''
        
        if (typeof item === 'string') {
            return item
        }
        
        if (typeof item === 'object') {
            return item.url || item.image || item.path || ''
        }
        
        return ''
    }
    
    // 基础URL拼接函数
    const buildSingleUrl = (item: ImageInput): string => {
        const raw = extractPath(item).trim()
        if (!raw) return ''

        if (/^data:/i.test(raw) || /^blob:/i.test(raw)) {
            return raw
        }

        const isAbsoluteUrl = /^(?:https?:)?\/\//i.test(raw)
        let mediaPath = raw.match(/^\/?media\/+(.+)$/i)?.[1] || ''

        // 历史接口可能返回完整的本站 /media/ URL；该地址也必须切换到公共存储。
        if (!mediaPath && isAbsoluteUrl) {
            try {
                const parsed = new URL(raw.startsWith('//') ? `https:${raw}` : raw)
                mediaPath = parsed.pathname.match(/^\/media\/+(.+)$/i)?.[1] || ''
                if (mediaPath) mediaPath += `${parsed.search}${parsed.hash}`
            } catch {
                return raw
            }
        }

        if (mediaPath && publicBase) {
            return `${publicBase.replace(/\/$/, '')}/${mediaPath}`
        }

        // 其他完整 URL（包括已经位于 COS 的地址）保持不变。
        if (isAbsoluteUrl) {
            return raw
        }

        // 统一去掉开头的 /media/ 以避免重复拼接
        const path = (mediaPath || raw).replace(/^\/?media\/+/, '').replace(/^\//, '')

        // 优先使用显式的文件基础URL（建议设置为完整的媒体根，如 http://host:port/media）
        if (publicBase) {
            return `${publicBase.replace(/\/$/, '')}/${path}`
        }

        // 其次使用 API 基础地址 + /media 前缀（当上面的未配置时）
        const apiBase = import.meta.env.VITE_API_URL as string | undefined
        if (apiBase) {
            return `${apiBase.replace(/\/$/, '')}/media/${path}`
        }

        // 最后回退到相对路径（需要本地开发服务器正确代理 /media 到后端）
        return `/media/${path}`
    }
    
    // 如果是数组，返回数组
    if (Array.isArray(input)) {
        return input.map(item => buildSingleUrl(item)).filter(url => url !== '')
    }
    
    // 如果是单个值，返回字符串
    return buildSingleUrl(input)
}
